import * as React from 'react';
import { CheckCircle2, ChevronDown, ChevronLeft } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { ErrorMessage } from '@/components/ui/error-message';
import { PageCarousel, type CarouselPage } from '@/components/ui/page-carousel';
import { Spinner } from '@/components/ui/spinner';
import { StatusChip } from '@/components/ui/status-chip';
import {
  Callout,
  KeyHint,
  WorkspaceButton,
  WorkspaceDialog,
  WorkspaceMenu,
} from '@/components/ui/workspace';
import type {
  EditorBlock,
  EditorCitation,
  RewriteRequest,
  RewriteResult,
  SaveBlockInput,
} from '@/components/report-editor';
import {
  newIdempotencyKey,
  rewriteReportBlock,
  usePublishVendorReport,
  useReportBlockMutations,
  vendorReportKey,
  type ReportCitation,
  type VendorReport,
} from '@/lib/api/vendor-intelligence-report';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/format';
import { DraftingCallout, EmptyReport, FocusRow } from './empty-report';
import {
  errorReason,
  moduleNamer,
  plural,
  proseIssueLines,
  publishBlocker,
  saveError,
  toEditorCitation,
} from './report-logic';
import { carouselPages, modelFromReport, type ReportModel } from './report-model';
import { ReportPageView, type ReportPageEditing } from './report-page';
import { byLine, type PrepareState } from './report-views';

/**
 * The draft being edited (T-0283; E24-design §9, amended 4 Nov 2026):
 * the same pages as the published view, in the shared block editor. One
 * bar above the carousel (back to the published report, the draft's chip
 * and byline, Prepare again, Publish) and one line of checks with jump
 * links. Publish confirms in a dialog; once it lands the step goes back to
 * the published report, now at the new version.
 *
 * Every number shown is the server's: figures are tokens the report fills,
 * tables are composed by code from the fit frozen at drafting.
 */
export function DraftView({
  engagementId,
  report,
  engagement,
  prepare,
  notice,
  hasPublished,
  onBack,
  onPublished,
  onOpenCitation,
}: {
  engagementId: string;
  report: VendorReport;
  engagement?: { client_name: string; name: string } | null;
  prepare: PrepareState;
  /** What the last Prepare said, shown once. */
  notice: string | null;
  /** Whether a published version exists to go back to. */
  hasPublished: boolean;
  onBack: () => void;
  onPublished: () => void;
  onOpenCitation: (citation: ReportCitation | null) => void;
}) {
  const qc = useQueryClient();
  const publish = usePublishVendorReport(engagementId);
  const { save, revert } = useReportBlockMutations(engagementId);
  const [publishKey, setPublishKey] = React.useState<string | null>(null);
  const [focusOpen, setFocusOpen] = React.useState(false);
  const [hintsOpen, setHintsOpen] = React.useState(false);
  const [current, setCurrent] = React.useState<string | undefined>(undefined);
  // Citations a rewrite proposal brought in from the page's pool, by key.
  const proposed = React.useRef(new Map<string, ReportCitation>());

  const draft = report.draft;
  const model: ReportModel | null = React.useMemo(() => modelFromReport(report), [report]);
  const modelRef = React.useRef(model);
  modelRef.current = model;
  const pages: CarouselPage[] = React.useMemo(() => (model ? carouselPages(model) : []), [model]);
  const currentKey = current && pages.some((p) => p.key === current) ? current : pages[0]?.key;

  const onRewrite = React.useCallback(
    async (blockId: string, request: RewriteRequest): Promise<RewriteResult> => {
      const result = await rewriteReportBlock(engagementId, blockId, request);
      for (const c of result.proposal?.citations ?? []) proposed.current.set(c.key, c);
      const moduleName = moduleNamer(modelRef.current?.fit);
      return {
        proposal: result.proposal
          ? {
              text: result.proposal.text,
              citations: result.proposal.citations.map((c) => toEditorCitation(c, moduleName)),
            }
          : null,
        issues: result.issues,
        instruction: result.instruction,
      };
    },
    [engagementId],
  );

  const onSave = React.useCallback(
    async (blockId: string, input: SaveBlockInput) => {
      try {
        await save.mutateAsync({
          blockId,
          request: {
            text: input.text,
            origin: input.origin,
            ...(input.instruction ? { instruction: input.instruction } : {}),
            ...(input.citations ? { citation_keys: input.citations } : {}),
          },
        });
      } catch (err) {
        // A published report's block: read again, the new draft is the one to edit.
        if (errorReason(err) === 'report.published') {
          void qc.invalidateQueries({ queryKey: vendorReportKey(engagementId) });
        }
        throw saveError(err);
      }
    },
    [save, qc, engagementId],
  );

  const onRevert = React.useCallback(
    async (blockId: string) => {
      await revert.mutateAsync(blockId);
    },
    [revert],
  );

  const openCitation = React.useCallback(
    (c: EditorCitation, _block: EditorBlock) => {
      onOpenCitation(modelRef.current?.citations.get(c.id) ?? proposed.current.get(c.id) ?? null);
    },
    [onOpenCitation],
  );

  const editing: ReportPageEditing = React.useMemo(
    () => ({
      readOnly: !report.can_edit,
      onRewrite,
      onSave,
      onRevert,
      onOpenCitation: openCitation,
    }),
    [report.can_edit, onRewrite, onSave, onRevert, openCitation],
  );

  if (!draft || !model) {
    return (
      <>
        <div>
          <WorkspaceButton variant="tertiary" className="-ml-2.5" onClick={onBack}>
            <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
            {hasPublished ? 'Back to published' : 'Back'}
          </WorkspaceButton>
        </div>
        <EmptyReport report={report} prepare={prepare} onOpenDraft={() => {}} />
      </>
    );
  }

  const busy = prepare.pending || publish.isPending;
  const issues = proseIssueLines(model.pages);
  const blocker = publishBlocker(report);
  const prepareBlocked = prepare.blocker?.message ?? null;
  const hasChecks = report.stale || draft.refusals > 0 || issues.length > 0 || prepareBlocked;

  const jumpTo = (blockId: string) => {
    const page = model.pages.find((p) => p.blocks.some((b) => b.id === blockId));
    if (page) setCurrent(page.key);
    // The page must be current (and no longer inert) before its block can take focus.
    requestAnimationFrame(() => {
      const el = document.querySelector<HTMLElement>(`[data-block-id="${blockId}"]`);
      el?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      el?.focus();
    });
  };

  const runPublish = () => {
    if (!publishKey) return;
    publish.mutate(publishKey, {
      onSuccess: () => {
        setPublishKey(null);
        onPublished();
      },
    });
  };

  const header = (
    <div className="flex flex-col gap-2" data-testid="vi-report-draft-bar">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <WorkspaceButton variant="tertiary" className="-ml-2.5" onClick={onBack}>
          <ChevronLeft className="h-3.5 w-3.5" aria-hidden="true" />
          {hasPublished ? 'Back to published' : 'Back'}
        </WorkspaceButton>
        <StatusChip tone="amber" size="sm">
          Draft · version {draft.version}
        </StatusChip>
        <span className="text-meta text-ink-500">
          Drafted {byLine(formatDate(draft.drafted_at), draft.drafted_by.name)}
          {draft.focus ? ` · focus: ${draft.focus}` : ''}
          {pages.length > 0 ? ` · ${plural(pages.length, 'page')}` : ''}
        </span>
        {report.can_edit ? (
          <div className="ml-auto flex flex-wrap items-center gap-2">
            <WorkspaceButton
              variant="tertiary"
              aria-label="Keyboard shortcuts"
              aria-expanded={hintsOpen}
              onClick={() => setHintsOpen((v) => !v)}
              className="hidden w-[30px] px-0 font-mono lg:inline-flex"
            >
              ?
            </WorkspaceButton>
            <WorkspaceMenu
              aria-label="Prepare again"
              align="end"
              trigger={
                <WorkspaceButton
                  disabled={busy || prepareBlocked !== null}
                  title={prepareBlocked ?? undefined}
                >
                  {prepare.pending ? <Spinner className="h-3.5 w-3.5" /> : null}
                  Prepare again
                  <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
                </WorkspaceButton>
              }
              items={[
                {
                  id: 'again',
                  label: 'Prepare again',
                  hint: 'keeps your edits',
                  onSelect: () => {
                    prepare.setFocus('');
                    prepare.run();
                  },
                },
                {
                  id: 'focus',
                  label: 'Prepare again with a focus…',
                  onSelect: () => setFocusOpen(true),
                },
              ]}
            />
            {report.can_publish ? (
              <WorkspaceButton
                variant="primary"
                disabled={busy || blocker !== null}
                title={blocker ?? undefined}
                onClick={() => setPublishKey(newIdempotencyKey())}
              >
                Publish
              </WorkspaceButton>
            ) : null}
          </div>
        ) : null}
      </div>

      {focusOpen && report.can_edit ? (
        <FocusRow
          prepare={prepare}
          action="Prepare with this focus"
          onCancel={() => {
            prepare.setFocus('');
            setFocusOpen(false);
          }}
        />
      ) : null}

      {hintsOpen && report.can_edit ? (
        <p className="m-0 flex flex-wrap items-center gap-x-3 gap-y-1 text-meta text-ink-500">
          <KeyHint keys={['←', '→']}>page</KeyHint>
          <KeyHint keys={['j', 'k']}>move</KeyHint>
          <KeyHint keys={['e']}>edit</KeyHint>
          <KeyHint keys={['r']}>rewrite</KeyHint>
          <KeyHint keys={['Esc']}>close</KeyHint>
        </p>
      ) : null}

      {hasChecks ? (
        <div
          data-testid="vi-report-checks"
          className={cn(
            'flex flex-wrap items-baseline gap-x-2 gap-y-1 border-l-2 pl-2.5 text-body-sm text-ink-700',
            report.stale || draft.refusals > 0 || prepareBlocked
              ? 'border-accent-amber-600'
              : 'border-ink-200',
          )}
        >
          <StatusChip tone="amber" size="sm">
            Checks
          </StatusChip>
          {prepareBlocked ? <span>Prepare again waits: {prepareBlocked}</span> : null}
          {report.stale ? (
            <span>
              The confirmed ratings or the shortlist changed after this draft was prepared: Prepare
              again to bring the figures up to date; paragraphs you edited or rewrote are kept.
            </span>
          ) : null}
          {draft.refusals > 0 ? (
            <span>
              {plural(draft.refusals, 'paragraph')} could not be drafted from cited evidence and{' '}
              {draft.refusals === 1 ? 'is' : 'are'} marked on{' '}
              {draft.refusals === 1 ? 'its' : 'their'} page: write{' '}
              {draft.refusals === 1 ? 'it' : 'them'} before publishing.
            </span>
          ) : null}
          {issues.length > 0 ? (
            <span data-testid="report-prose-issues">
              {issues.length === 1
                ? 'One thing in the text is not a figure from the ratings'
                : `${issues.length} things in the text are not figures from the ratings`}{' '}
              (numbers belong in figures, which the report fills from code; you can still publish):{' '}
              {issues.map((line, i) => (
                <React.Fragment key={`${line.blockId}-${i}`}>
                  {i > 0 ? '; ' : ''}
                  <button
                    type="button"
                    onClick={() => jumpTo(line.blockId)}
                    className="text-left underline-offset-2 hover:underline"
                  >
                    <b className="font-semibold text-ink-900">{line.sectionTitle}</b>: {line.text}
                  </button>
                </React.Fragment>
              ))}
            </span>
          ) : null}
        </div>
      ) : null}

      {prepare.pending ? <DraftingCallout /> : null}
      {prepare.error ? <ErrorMessage error={prepare.error} /> : null}
      {publish.error ? <ErrorMessage error={publish.error} /> : null}
      {notice && !prepare.pending ? (
        <Callout tone="ok" icon={CheckCircle2} role="status">
          {notice}
        </Callout>
      ) : null}
    </div>
  );

  return (
    <>
      <PageCarousel
        aria-label="Report pages"
        header={header}
        pages={pages}
        current={currentKey}
        onChange={(key) => setCurrent(key)}
        renderPage={(page, index) => {
          const pageModel = model.pages.find((p) => p.key === page.key);
          return pageModel ? (
            <ReportPageView
              model={model}
              page={pageModel}
              index={index}
              count={pages.length}
              editing={editing}
              engagement={engagement}
            />
          ) : null;
        }}
        className="min-h-0 flex-1"
      />
      <WorkspaceDialog
        open={publishKey !== null}
        onOpenChange={(open) => {
          if (!open && !publish.isPending) setPublishKey(null);
        }}
        title={`Publish version ${draft.version}?`}
        description="A published version never changes; the next draft starts as a copy."
        actions={
          <>
            <WorkspaceButton
              variant="tertiary"
              size="md"
              disabled={publish.isPending}
              onClick={() => setPublishKey(null)}
            >
              Cancel
            </WorkspaceButton>
            <WorkspaceButton
              variant="primary"
              size="md"
              disabled={publish.isPending}
              onClick={runPublish}
            >
              {publish.isPending ? <Spinner className="h-3.5 w-3.5" /> : null}
              Publish version {draft.version}
            </WorkspaceButton>
          </>
        }
      >
        <p className="m-0 text-body-sm text-ink-700">
          Publishing freezes these {plural(pages.length, 'page')} as version {draft.version} and
          renders the PDF for the client, citing every record by its id.
          {issues.length > 0
            ? ` ${issues.length === 1 ? 'One number is' : `${issues.length} numbers are`} typed rather than figures.`
            : ''}
        </p>
        {publish.error ? <ErrorMessage error={publish.error} /> : null}
      </WorkspaceDialog>
    </>
  );
}
